import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {
  IUserAccessFeature,
  IUserAccessSubFeatures,
  TFeatures,
} from '../../../user-access/models/user-access-features/user-access-features';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IStore} from 'src/app/shared/shared-models/store/store';
import {Store} from '@ngrx/store';
import {SettingsReferenceEnum} from '../../models';
import {settingsSetSelected} from '../../store/setting.actions';
import {_DEFAULT_CRUD_OPTIONS, ICrudOptions} from '../../../user-access/models/user-access-features/access';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {UnsavedChangesService} from '../../services';
import {NavigationPageEnum, NavigationPageType} from '../../../user-access/models/navigations.enums';

@Component({
  selector: 'app-settings-layout-side-bar',
  templateUrl: './settings-layout-side-bar.component.html',
  styleUrls: ['./settings-layout-side-bar.component.scss'],
  standalone: false,
})
export class SettingsLayoutSideBarComponent {

  private readonly _store = inject(Store);
  private readonly _unsavedChangesService = inject(UnsavedChangesService);

  _selectedSection: string = 'Account';
  _selectedSubSection: string = '';
  _selectedStore: IStore = <IStore>{};
  _selectedSetting: SettingsReferenceEnum = SettingsReferenceEnum.undefined;
  _collapsed = false;

  #actionBarCrud: ICrudOptions = {..._DEFAULT_CRUD_OPTIONS};
  #sectionFeatures: TFeatures[] = [];
  #openAccordionNames = new Set<string>();

  protected readonly icons = IconsEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;
  protected readonly iconColours = IconColourEnum;

  private readonly featureIcons: Partial<Record<NavigationPageType, IconsEnum>> = {
    [NavigationPageEnum.AutoOrdering]: IconsEnum.cartOutline,
    [NavigationPageEnum.CopySettings]: IconsEnum.contentCopy,
    [NavigationPageEnum.LabelMaker]: IconsEnum.priceTags,
    [NavigationPageEnum.ShelfTalkers]: IconsEnum.printerOutline,
    [NavigationPageEnum.StockRules]: IconsEnum.filterVariant,
    [NavigationPageEnum.StockTake]: IconsEnum.clipboardCheckOutline,
    [NavigationPageEnum.LocalStorage]: IconsEnum.folder,
  };

  @Output() readonly expandRequest = new EventEmitter<void>();

  get actionBarCRUD(): ICrudOptions {
    return this.#actionBarCrud;
  }

  get sectionFeatures(): TFeatures[] {
    return this.#sectionFeatures;
  }

  get selectedSection(): string {
    return this._selectedSection;
  }

  get selectedSubSection(): string {
    return this._selectedSubSection;
  }

  get selectedSetting(): SettingsReferenceEnum {
    return this._selectedSetting;
  }

  get selectedStore(): IStore {
    return this._selectedStore;
  }

  get collapsed(): boolean {
    return this._collapsed;
  }

  @Input() set sectionFeatures(features: TFeatures[]) {
    this.#sectionFeatures = features || [];
  }

  @Input() set selectedSection(section: string) {
    this._selectedSection = section;
  }

  @Input() set selectedSubSection(section: string) {
    this._selectedSubSection = section;
    if (section) {
      this.#openAccordionNames.add(section);
    }
  }

  @Input() set selectedSetting(setting: SettingsReferenceEnum) {
    this._selectedSetting = setting;
  }

  @Input() set selectedStore(selectedStore: IStore) {
    this._selectedStore = selectedStore ?? <IStore>{};
  }

  @Input() set collapsed(value: boolean) {
    this._collapsed = value;
  }

  /**
   * Icon for a Feature menu entry when the sidebar is collapsed to a rail.
   */
  getFeatureIcon(setting: TFeatures): IconsEnum {
    return this.featureIcons[setting.feature] ?? IconsEnum.cogOutline;
  }

  /**
   * Accordion open state is preserved across sidebar collapse/expand.
   * While collapsed the body is hidden; when expanded, previously open groups reopen.
   */
  isAccordionOpen(displayText: string): boolean {
    return !this._collapsed && this.#openAccordionNames.has(displayText);
  }

  /**
   * Tracks user toggles of Feature accordion groups while the sidebar is expanded.
   */
  onAccordionToggled(displayText: string, isOpen: boolean): void {
    if (this._collapsed) {
      return;
    }
    if (isOpen) {
      this.#openAccordionNames.add(displayText);
    } else {
      this.#openAccordionNames.delete(displayText);
    }
  }

  /**
   * Handles click on a subsection setting (nested within a section)
   * Checks for unsaved changes before navigating
   */
  async onClickSubSetting(settingRef: SettingsReferenceEnum, sectionHeader: string, subSectionName: string): Promise<void> {
    const canProceed: boolean = await this._unsavedChangesService.checkUnsavedChanges();
    if (!canProceed) {
      return;
    }

    this._selectedSection = sectionHeader;
    this._selectedSubSection = subSectionName;
    this._selectedSetting = settingRef;
    this.#openAccordionNames.add(subSectionName);

    this._store.dispatch(settingsSetSelected.setSelectedReference({ref: settingRef}));
    this._store.dispatch(settingsSetSelected.setSelectedSection({ref: sectionHeader}));
    this._store.dispatch(settingsSetSelected.setSelectedSubSection({ref: subSectionName}));
  }

  /**
   * Handles click on a standalone setting (directly under a section)
   * Checks for unsaved changes before navigating
   */
  async onClickStandaloneSetting(settingRef: SettingsReferenceEnum, sectionHeader: string): Promise<void> {
    const canProceed: boolean = await this._unsavedChangesService.checkUnsavedChanges();
    if (!canProceed) {
      return;
    }

    this._selectedSection = sectionHeader;
    this._selectedSubSection = '';
    this._selectedSetting = settingRef;

    this._store.dispatch(settingsSetSelected.setSelectedReference({ref: settingRef}));
    this._store.dispatch(settingsSetSelected.setSelectedSection({ref: sectionHeader}));
    this._store.dispatch(settingsSetSelected.setSelectedSubSection({ref: ''}));
  }

  /**
   * Whether a Feature entry (or one of its sub-features) is the active setting.
   */
  isFeatureSelected(setting: TFeatures): boolean {
    if (setting.standalone === false) {
      return !!setting.subFeatures?.some(
        (sub: IUserAccessSubFeatures) => this.forceTypeExtend(sub).ref === this._selectedSetting,
      );
    }
    return this._selectedSetting === setting.ref;
  }

  /**
   * When collapsed, expand the rail and open the clicked accordion group.
   */
  onCollapsedRailClick(event: Event, accordionName?: string): void {
    if (!this._collapsed) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (accordionName) {
      this.#openAccordionNames.add(accordionName);
    }
    this.expandRequest.emit();
  }

  forceTypeExtend(setting: IUserAccessSubFeatures): IUserAccessFeature {
    return setting as unknown as IUserAccessFeature;
  }

}
